import { useQueryClient } from "@tanstack/react-query";
import { FC, useState } from "react";
import { FormattedMessage } from "react-intl";
import { User } from "~/api/model";

import { getListUsersQueryKey, useReactivateUser } from "~/api/users/users";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import { Button } from "~/components/ui/button";
import { useSettingsParams } from "~/helpers";

interface ReactivateUserDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  user: User;
}

enum ReactivateUserDialogStep {
  ReactivateUser = 1,
  Success = 2,
  Error = 3,
}

export const ReactivateUserDialog: FC<ReactivateUserDialogProps> = ({
  open,
  setOpen,
  user,
}) => {
  const [step, setStep] = useState<ReactivateUserDialogStep>(
    ReactivateUserDialogStep.ReactivateUser
  );
  const { organizationId } = useSettingsParams();
  const queryClient = useQueryClient();

  const { mutate: reactivateUser, isPending } = useReactivateUser();

  const onSubmit = () => {
    reactivateUser(
      { organizationId, userId: user.id },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: getListUsersQueryKey(organizationId),
          });
          setStep(ReactivateUserDialogStep.Success);
        },
        onError: () => {
          setStep(ReactivateUserDialogStep.Error);
        },
      }
    );
  };

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {step === ReactivateUserDialogStep.ReactivateUser && (
              <FormattedMessage
                id="settings.team.reactivateMember"
                defaultMessage="Reactivate Team Member"
              />
            )}
            {step === ReactivateUserDialogStep.Success && (
              <FormattedMessage id="success" defaultMessage="Success" />
            )}
            {step === ReactivateUserDialogStep.Error && (
              <FormattedMessage id="error" defaultMessage="Error" />
            )}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {step === ReactivateUserDialogStep.ReactivateUser && (
              <FormattedMessage
                id="settings.team.reactivateMember.confirmation"
                values={{
                  b: (chunks) => <strong>{chunks}</strong>,
                  userName: user.name,
                  userEmail: user.email,
                }}
              />
            )}
            {step === ReactivateUserDialogStep.Success && (
              <FormattedMessage
                id="settings.team.reactivate.success"
                values={{
                  b: (chunks) => <strong>{chunks}</strong>,
                  userName: user.name,
                  userEmail: user.email,
                }}
              />
            )}
            {step === ReactivateUserDialogStep.Error && (
              <FormattedMessage
                id="settings.team.invite.error"
                defaultMessage="Something went wrong. Please try again later."
              />
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        {step === ReactivateUserDialogStep.ReactivateUser && (
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setOpen(false)}>
              <FormattedMessage id="cancel" defaultMessage="Cancel" />
            </Button>
            <Button onClick={onSubmit} disabled={isPending}>
              <FormattedMessage
                id="settings.team.reactivate"
                defaultMessage="Reactivate"
              />
            </Button>
          </div>
        )}
        {step !== ReactivateUserDialogStep.ReactivateUser && (
          <Button
            variant="outline"
            className="ml-auto"
            onClick={() => setOpen(false)}
          >
            <FormattedMessage id="close" defaultMessage="Close" />
          </Button>
        )}
      </AlertDialogContent>
    </AlertDialog>
  );
};
